/* eslint-disable import/prefer-default-export */
import * as Sentry from '@sentry/nextjs';
import { v4 as uuidv4 } from 'uuid';
import logger from '@utils/logger';
import { LOG_TYPE } from '@const/index';
import getPublicEnv from '@utils/getPublicEnv';

let openedConnections = 0;

const redactHeaders = (options: RequestInit | undefined): RequestInit | undefined => {
  if (!options?.headers) return options;

  const headers: Record<string, string> = {};
  const redactKeys = ['key', 'secret', 'authorization'];

  const redactValue = (key: string, value: string): string => {
    const keyLower = key.toLowerCase();
    const isKeyToRedact = redactKeys.some((redactKey) => keyLower.includes(redactKey));
    return isKeyToRedact ? '[redacted]' : value;
  };

  if (options.headers instanceof Headers) {
    options.headers.forEach((value, key) => {
      headers[key] = redactValue(key, value);
    });
  } else {
    Object.keys(options.headers).forEach((header) => {
      headers[header] = redactValue(header, options.headers?.[header] as string);
    });
  }

  return { ...options, headers };
};

const requestLog = async (
  res: Response,
  json: Record<string, unknown> | null,
  path: RequestInfo | URL,
  options: RequestInit | undefined,
  correlationId: string,
  time = 0,
) => {
  const logObject = {
    type: LOG_TYPE.requestToExternalService,
    path,
    options: redactHeaders(options),
    correlationId,
    status: res.status,
    openedConnections,
    processTime: time,
  };

  const pathString = String(path);
  const isVerboseListResponse =
    pathString.includes('/pos/point-of-sale') || pathString.includes('/available-points-of-delivery');

  if (res.status >= 400 && res.status < 500) {
    logger.error({ ...logObject, response: json || '' });
  } else if (res.status >= 500) {
    logger.error({ ...logObject, response: json || '' });
  } else if (!isVerboseListResponse && !process.env.MUTE_LOGS && process.env.MUTE_LOGS !== 'true') {
    logger.info({ ...logObject, response: json || '' });
  }
};

export const fetcher = async (path: RequestInfo | URL, options?: RequestInit, retries = 1) => {
  const timeStart = Date.now();
  let isCorrelationIdSet = false;
  const headers = new Headers();
  let correlationId = uuidv4();
  const isServerSide = typeof window === 'undefined';
  const SERVICE_NAME = isServerSide ? process.env.SERVICE_NAME : getPublicEnv('NEXT_PUBLIC_SERVICE_NAME');
  const SERVICE_INSTANCE = isServerSide ? process.env.SERVICE_INSTANCE : getPublicEnv('NEXT_PUBLIC_SERVICE_INSTANCE');
  const SERVICE_VERSION = isServerSide ? process.env.SERVICE_VERSION : getPublicEnv('NEXT_PUBLIC_SERVICE_VERSION');

  const setExistingCorrelationId = (newCorrelationId: string) => {
    isCorrelationIdSet = true;
    correlationId = newCorrelationId;
  };

  if (options?.headers) {
    if (options.headers instanceof Headers) {
      options.headers.forEach((value, key) => {
        headers.append(key, value);

        if (key.toLowerCase() === 'x-correlation-id') setExistingCorrelationId(value);
      });
    } else {
      Object.keys(options.headers).forEach((header) => {
        headers.append(header, options?.headers?.[header]);

        if (header.toLowerCase() === 'x-correlation-id') setExistingCorrelationId(options?.headers?.[header]);
      });
    }
  }

  Sentry.getCurrentScope().setTag('correlation_id', correlationId);

  if (!isCorrelationIdSet) headers.append('X-Correlation-Id', correlationId);
  headers.append('X-Service-Name', SERVICE_NAME as string);
  headers.append('X-Service-Instance', SERVICE_INSTANCE as string);
  headers.append('X-Service-Version', SERVICE_VERSION as string);

  const initOptions: RequestInit = {
    ...options,
    headers,
  };

  openedConnections += 1;
  return fetch(path, initOptions)
    .then(async (res) => {
      openedConnections -= 1;
      const timeEnd = Date.now();
      const isJson =
        res.headers.get('content-type')?.includes('application/json') ||
        res.headers.get('content-type')?.includes('application/vnd.api+json');
      const json = isJson ? await res.json() : {};
      if (isServerSide) void requestLog(res, json, path, initOptions, correlationId, (timeEnd - timeStart) / 1000);

      return {
        json,
        status: res.status,
      };
    })
    .catch((error) => {
      openedConnections -= 1;
      if (error.code === 'ECONNRESET' && retries > 0) {
        logger.info('Retry due to ECONNRESET error');
        return fetcher(path, options, retries - 1);
      }

      // NOTE: graceful handling of failed fetch caused by cancelled requests
      if (error.message.includes('Failed to fetch') || error.message.includes('TypeError: Load failed')) {
        return { json: {}, status: 400 };
      }

      throw new Error(error);
    });
};
